import * as THREE from "three";
import TextVariables from "./TextVariables";
import MathUtils from "./utilities/MathUtils";
import TextUtils from "./utilities/TextUtils";
import Scenario from "./scenarioElements/Scenario";
import Engine from '../classes/Engine';
import { CustomEventTypes, defaultAnnotationObject} from "../globals";
import { IColor, IVector3 } from "../interfaces/Misc";
import { Annotation } from "../interfaces/Annotation";
import { TextAlign } from "../interfaces/enums";
import store from "../state/store";

const lookAtRotationCorrection = new THREE.Quaternion().setFromEuler(new THREE.Euler(90 / 180 * Math.PI, 0, 0));
const cameraUp = new THREE.Vector3();
const cameraRight = new THREE.Vector3();
const tempMat = new THREE.Matrix4();
const identityQuat = new THREE.Quaternion();

const topBottomPadding = 16;
const leftRightPadding = 18;
const titleLineSpacing = 24;
const descriptionLineSpacing = 24;
const titleTextSize = 50;
const descriptionTextSize = 32;
const strokeWidth = 0.0030;
const lineWidthConstant = 0.0005;

class Callout extends THREE.Sprite
{
	static calloutMap: Map<string, Callout> = new Map();
	ctx: CanvasRenderingContext2D;
	canvasTexture: THREE.CanvasTexture;
	widthScale: number;
	heightScale: number;
	spriteScale: THREE.Vector3;
	calloutLine: THREE.Mesh;
	strokePlanes: THREE.Mesh[];
	strokeMaterial: THREE.MeshBasicMaterial;
	modifiedTitle?: string;
	modifiedDescription?: string;

	static clearData()
	{
		Callout.calloutMap.clear();
	}

	static setCallout(objectGuid: string, callout?: Callout) : void
	{
		if (!callout)
		{
			Callout.calloutMap.delete(objectGuid);
		}
		else
		{
			Callout.calloutMap.set(objectGuid, callout);
		}
	}

	static getCallout(objectGuid: string) : Callout | undefined
	{
		return Callout.calloutMap.get(objectGuid);
	}

	static addCallout(object: THREE.Object3D)
	{
		let callout: Callout | undefined = undefined;

		if (!object.userData.annotation)
		{
			const defaultOffset = store.getState().preferencesModule.editorPreferences.defaultCalloutOffset;
			const defaultScale = store.getState().preferencesModule.editorPreferences.defaultCalloutScale;
	
			object.userData.annotation = JSON.parse(JSON.stringify(defaultAnnotationObject));
			object.userData.annotation.title = (TextVariables.variableMarker + "ObjectName");
			object.userData.annotation.scale = defaultScale;
			object.userData.annotation.offset = defaultOffset;

			callout = new Callout();
			Callout.setCallout(object.userData.guid, callout);
		}
		else
		{
			callout = Callout.getCallout(object.userData.guid);
			if (!callout)
			{
				callout = new Callout();
				Callout.setCallout(object.userData.guid, callout);
			}
			object.userData.annotation.enabled = true;
			callout.setVisible(object.userData.annotation.isVisible);
			if (callout.parent)
			{
				callout.setDescription(callout.getDescription());
				callout.setTitle(callout.getTitle());
			}

			if(object instanceof THREE.Sprite || !(object instanceof THREE.Mesh))
			{
				object.userData.annotation.originOffset = {x: 0, y: 0, z: 0};
			}
		}

		object.add(callout);
		callout.updateCanvas();
		callout.setBackgroundColor(object.userData.annotation.backgroundColor);
		callout.setLineColor(object.userData.annotation.lineColor);
		callout.setOffset(object.userData.annotation.offset);
		callout.setStrokeColor(object.userData.annotation.strokeColor);
		callout.setStrokeEnabled(object.userData.annotation.strokeEnabled);

		return callout;
	}


	static loadCallout(object: THREE.Object3D, annotation: Annotation)
	{
		object.userData.annotation = annotation;
		if (object.userData.annotation.enabled === undefined) object.userData.annotation.enabled = true;
		if (object.userData.annotation.lineColor === undefined) object.userData.annotation.lineColor = JSON.parse(JSON.stringify(object.userData.annotation.backgroundColor));
		if (object.userData.annotation.originOffset === undefined) object.userData.annotation.originOffset = {
			x: 0,
			y: 0,
			z: 0
		};
		if (object.userData.annotation.strokeEnabled === undefined) //callout was made before the WC20-3024 polish changes, need to set some default values and convert the some old values
		{
			object.userData.annotation.scale = object.userData.annotation.scale * 7; //old default was 0.1 (in meters) - this is sort of an approximation to mantain the same physical font size
			object.userData.annotation.width = 1;
			object.userData.annotation.autoWidth = true;
			object.userData.annotation.descriptionEnabled = true;
			object.userData.annotation.strokeEnabled = true;
			object.userData.annotation.strokeColor = JSON.parse(JSON.stringify(object.userData.annotation.backgroundColor));
			object.userData.annotation.titleTextAlign = TextAlign.Left;
			object.userData.annotation.descriptionTextAlign = TextAlign.Left;

			if (object.userData.annotation.titleBackground.a !== 100)
			{
				//Old implementation was drawing the title background on top of the description background, causing a title background with transparency to be affected by the description background's color
				//the backgrounds no longer overlap, so we need to combine the colors to get the same effect
				//important to note that the old implementation was wrongfully alpha blending in linear color space, which is why we need to convert to linear color space before blending, and convert the final output back to srgb
				const oldBackgroundColor = new THREE.Color().setRGB(object.userData.annotation.backgroundColor.r / 255, object.userData.annotation.backgroundColor.g / 255, object.userData.annotation.backgroundColor.b / 255).convertSRGBToLinear();
				const oldBackgroundColorAlpha = object.userData.annotation.backgroundColor.a / 100;
				const oldTitleBackgroundColor = new THREE.Color().setRGB(object.userData.annotation.titleBackground.r / 255, object.userData.annotation.titleBackground.g / 255, object.userData.annotation.titleBackground.b / 255).convertSRGBToLinear();
				const oldTitleBackgroundColorAlpha = object.userData.annotation.titleBackground.a / 100;

				const combinedAlpha = oldBackgroundColorAlpha + oldTitleBackgroundColorAlpha * (1 - oldBackgroundColorAlpha);
				if (combinedAlpha === 0)
				{
					object.userData.annotation.titleBackground.a = 0;
				}
				else
				{
					const combinedRed = ((oldTitleBackgroundColor.r * oldTitleBackgroundColorAlpha + oldBackgroundColor.r * oldBackgroundColorAlpha * (1 - oldTitleBackgroundColorAlpha)) / combinedAlpha);
					const combinedGreen = ((oldTitleBackgroundColor.g * oldTitleBackgroundColorAlpha + oldBackgroundColor.g * oldBackgroundColorAlpha * (1 - oldTitleBackgroundColorAlpha)) / combinedAlpha);
					const combinedBlue = ((oldTitleBackgroundColor.b * oldTitleBackgroundColorAlpha + oldBackgroundColor.b * oldBackgroundColorAlpha * (1 - oldTitleBackgroundColorAlpha)) / combinedAlpha);
					const combinedColor = new THREE.Color().setRGB(combinedRed, combinedGreen, combinedBlue).convertLinearToSRGB();

					object.userData.annotation.titleBackground.r = Math.floor(combinedColor.r * 255);
					object.userData.annotation.titleBackground.g = Math.floor(combinedColor.g * 255);
					object.userData.annotation.titleBackground.b = Math.floor(combinedColor.b * 255);
					object.userData.annotation.titleBackground.a = Math.floor(combinedAlpha * 100);
				}
			}
		}

		const currentCallout = Callout.getCallout(object.userData.guid);
		const callout = currentCallout ?? new Callout();
		if(!currentCallout)
		{
			object.add(callout);
			Callout.setCallout(object.userData.guid, callout);
		}
		
		if(object instanceof THREE.Sprite || !(object instanceof THREE.Mesh))
		{
			object.userData.annotation.originOffset = {x: 0, y: 0, z: 0};
		}

		callout.updateCanvas();
		callout.setBackgroundColor(object.userData.annotation.backgroundColor);
		callout.setLineColor(object.userData.annotation.lineColor);
		callout.setOffset(object.userData.annotation.offset);
		callout.setOriginOffset(object.userData.annotation.originOffset);
		callout.setVisible(object.userData.annotation.isVisible);
		callout.setStrokeColor(object.userData.annotation.strokeColor);
		callout.setStrokeEnabled(object.userData.annotation.strokeEnabled);

		if (!object.userData.annotation.enabled)
		{
			Callout.removeCallout(object);
		}
	}

	static removeCallout(object: THREE.Object3D)
	{
		const callout = Callout.getCallout(object.userData.guid);
		if (callout)
		{
			object.userData.annotation.enabled = false;
			callout.visible = false;
		}
	}

	constructor()
	{
		super();

		this.ctx = document.createElement('canvas').getContext('2d')!;

		this.canvasTexture = new THREE.CanvasTexture(this.ctx.canvas, THREE.UVMapping, THREE.ClampToEdgeWrapping, THREE.ClampToEdgeWrapping, THREE.LinearFilter, THREE.LinearMipmapLinearFilter);
		this.canvasTexture.colorSpace = THREE.SRGBColorSpace;

		const labelMaterial = new THREE.SpriteMaterial({
			map: this.canvasTexture,
			transparent: true,
		});

		this.material = labelMaterial;
		this.center = new THREE.Vector2(0.5, 0.5);
		this.name = "Callout";
		this.renderOrder = -1;
		this.userData.hideInHierarchy = true;
		this.userData.ignoreExport = true;
		this.widthScale = 1;
		this.heightScale = 1;
		this.spriteScale = new THREE.Vector3(1, 1, 1);

		this.calloutLine = new THREE.Mesh(new THREE.CylinderGeometry(1, 1), new THREE.MeshBasicMaterial({transparent: true}));
		this.calloutLine.renderOrder = -1;
		this.calloutLine.userData.hideInHierarchy = true;
		this.calloutLine.userData.ignoreExport = true;
		this.add(this.calloutLine);

		this.strokePlanes = [];
		this.strokeMaterial = new THREE.MeshBasicMaterial({color: 0x000000, transparent: true});
		for (let i = 0; i < 4; i++)
		{
			const strokePlane = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this.strokeMaterial);
			strokePlane.material.transparent = true;
			strokePlane.userData.hideInHierarchy = true;
			strokePlane.userData.ignoreExport = true;
			strokePlane.renderOrder = -1;
			this.add(strokePlane);
			this.strokePlanes.push(strokePlane);
		}

		document.addEventListener(CustomEventTypes.onElementNameChanged, this.handleUpdateEvents);
		document.addEventListener(CustomEventTypes.onIOTValueUpdated, this.handleUpdateEvents);
		document.addEventListener(CustomEventTypes.objectNameChanged, this.handleObjectPropertyUpdate);
		document.addEventListener(CustomEventTypes.objectMetadataValueChanged, this.handleObjectPropertyUpdate);
	}

	onDispose = () =>
	{
		document.removeEventListener(CustomEventTypes.onElementNameChanged, this.handleUpdateEvents);
		document.removeEventListener(CustomEventTypes.objectNameChanged, this.handleObjectPropertyUpdate);
		document.removeEventListener(CustomEventTypes.onIOTValueUpdated, this.handleUpdateEvents);
		document.removeEventListener(CustomEventTypes.objectMetadataValueChanged, this.handleObjectPropertyUpdate);

		const callout = Callout.getCallout(this.parent?.userData?.guid);
		if (callout)
		{
			Callout.calloutMap.delete(this.parent?.userData.guid);
		}
	}

	updateCanvas = () =>
	{
		if (!this.modifiedDescription && this.modifiedDescription !== '') this.setDescription(this.getDescription());
		if (!this.modifiedTitle && this.modifiedTitle !== '') this.setTitle(this.getTitle());
		if ( !this.parent ) return;

		const annotation = this.parent.userData.annotation;

		const titleFont = `${titleTextSize}px Roboto`;
		const descriptionFont = `${descriptionTextSize}px Roboto`;

		const annotationWidthPixels = annotation.width * 256;
		const strokeWidthPixels = annotation.strokeEnabled ? strokeWidth * 256 * 10 : 0;

		this.ctx.font = titleFont;
		const titleMetrics = this.ctx.measureText('hg');
		const titleLineHeight = titleMetrics.actualBoundingBoxAscent;
		let titletLines: string[] = [];
		let titleWidth = 0;
		const inputTitleLines = this.modifiedTitle?.split(/\r?\n/);
		if (inputTitleLines)
		{
			if (annotation.autoWidth)
			{
				let maxWidth = 0;
				titletLines = inputTitleLines;
				for (const line of inputTitleLines)
				{
					const metrics = this.ctx.measureText(line);
					if (metrics.width > maxWidth) maxWidth = metrics.width;
				}
				titleWidth = maxWidth + leftRightPadding * 2;
			}
			else
			{
				titleWidth = annotationWidthPixels;
				for (const line of inputTitleLines)
				{
					titletLines = titletLines.concat(TextUtils.wrapContextText(this.ctx, titleFont, line, annotationWidthPixels - leftRightPadding * 2 - strokeWidthPixels * 2));
				}
			}
		}

		const titleHeight = (titleLineHeight * titletLines.length) + ((titletLines.length - 1) * titleLineSpacing) + topBottomPadding * 2;

		this.ctx.font = descriptionFont;
		const descriptionMetrics = this.ctx.measureText('hg');
		const descriptionLineHeight = descriptionMetrics.actualBoundingBoxAscent;
		let descriptionLines: string[] = [];
		let descriptionWidth = 0;
		let descriptionHeight = 0;

		if (annotation.descriptionEnabled && this.modifiedDescription)
		{
			const inputDescriptionLines = this.modifiedDescription.split(/\r?\n/);
			if (annotation.autoWidth)
			{
				let maxWidth = 0;
				descriptionLines = inputDescriptionLines;
				for (const line of descriptionLines)
				{
					const metrics = this.ctx.measureText(line);
					if (metrics.width > maxWidth) maxWidth = metrics.width;
				}
				descriptionWidth = maxWidth + leftRightPadding * 2;
			}
			else
			{
				descriptionWidth = annotationWidthPixels;
				for (const line of inputDescriptionLines)
				{
					descriptionLines = descriptionLines.concat(TextUtils.wrapContextText(this.ctx, descriptionFont, line, annotationWidthPixels - leftRightPadding * 2 - strokeWidthPixels * 2));
				}
			}
			descriptionHeight = (descriptionLineHeight * descriptionLines.length) + ((descriptionLines.length - 1) * descriptionLineSpacing) + topBottomPadding * 2;

		}

		const canvasWidth = annotation.autoWidth ? Math.max(titleWidth, descriptionWidth) : annotationWidthPixels - strokeWidthPixels * 2;
		const canvasHeight = titleHeight + descriptionHeight;

		//sprite is 1m x 1m by default. we decided that we want our callouts to be 256px/10cm wide by default.
		//this means that for our default values annotation.scale 1 & annotation.width 256 - we want to scale the sprite by 0.1 to make it 10cm wide.
		this.widthScale = canvasWidth / (256 * 10);
		this.heightScale = (canvasHeight / canvasWidth) * this.widthScale;
		this.spriteScale.set(annotation.scale * this.widthScale, annotation.scale * this.heightScale, 1);
		this.ctx.canvas.width = canvasWidth;
		this.ctx.canvas.height = canvasHeight;

		//title background
		this.ctx.fillStyle = `rgba(${annotation.titleBackground.r} , ${annotation.titleBackground.g}, ${annotation.titleBackground.b}, ${annotation.titleBackground.a / 100})`;
		this.ctx.fillRect(0, 0, this.ctx.canvas.width, titleHeight);

		//title
		this.ctx.font = titleFont;
		this.ctx.fillStyle = `rgb(${annotation.titleColor.r} , ${annotation.titleColor.g}, ${annotation.titleColor.b})`;
		this.ctx.textAlign = annotation.titleTextAlign;

		const titleX = Math.floor((annotation.titleTextAlign === TextAlign.Left) ? leftRightPadding :
			(annotation.titleTextAlign === TextAlign.Center) ? canvasWidth / 2 :
				canvasWidth - leftRightPadding);

		for (let i = 0; i < titletLines.length; i++)
		{
			const titleY = topBottomPadding + titleLineHeight * i + titleLineHeight + titleLineSpacing * i;
			this.ctx.fillText(titletLines[i], titleX, titleY);
		}

		if (annotation.descriptionEnabled)
		{
			//description background
			this.ctx.fillStyle = `rgba(${annotation.backgroundColor.r} , ${annotation.backgroundColor.g}, ${annotation.backgroundColor.b}, ${annotation.backgroundColor.a / 100})`;
			this.ctx.fillRect(0, titleHeight, this.ctx.canvas.width, descriptionHeight);

			//description
			this.ctx.font = descriptionFont;
			this.ctx.fillStyle = `rgb(${annotation.textColor.r} , ${annotation.textColor.g}, ${annotation.textColor.b})`;
			this.ctx.textAlign = annotation.descriptionTextAlign;

			const descriptionX = Math.floor((annotation.descriptionTextAlign === TextAlign.Left) ? leftRightPadding :
				(annotation.descriptionTextAlign === TextAlign.Center) ? canvasWidth / 2 :
					canvasWidth - leftRightPadding);

			for (let i = 0; i < descriptionLines.length; i++)
			{
				const descriptionY = titleHeight + (topBottomPadding + descriptionLineHeight * i + descriptionLineHeight + descriptionLineSpacing * i);
				this.ctx.fillText(descriptionLines[i], descriptionX, descriptionY);
			}
		}

		//this.canvasTexture.needsUpdate = true;
		//unhappy with this, but simply calling  this.canvasTexture.needsUpdate = true stopped working with the threejs upgrade.
		//looks like it no longer supports cases where the size of the canvas changes, so we need to dispose of the old texture and create a new one

		if (this.canvasTexture) this.canvasTexture.dispose();
		this.canvasTexture = new THREE.CanvasTexture(this.ctx.canvas, THREE.UVMapping, THREE.ClampToEdgeWrapping, THREE.ClampToEdgeWrapping, THREE.LinearFilter, THREE.LinearMipmapLinearFilter);
		this.canvasTexture.colorSpace = THREE.SRGBColorSpace;
		this.material.map = this.canvasTexture;
	}

	handleObjectPropertyUpdate = (args) =>
	{
		if(!this.parent) return;
		if (args?.detail?.objects)
		{
			for (const object of args.detail.objects)
			{
				if (object.userData.guid === this.parent?.userData.guid)
				{
					this.setTitle(this.getTitle());
					this.setDescription(this.getDescription());
					break;
				}
			}
		}
	}

	handleUpdateEvents = () =>
	{
		if(!this.parent) return;
		this.setTitle(this.getTitle());
		this.setDescription(this.getDescription());
	}



	getVisible = () => 
	{
		return this.visible;
	}

	setVisible = (value: boolean) =>
	{
		this.visible = value;
		if (this.parent)
		{
			this.parent.userData.annotation.isVisible = value;
		}
	}

	getTitle = () =>
	{
		return this.parent?.userData.annotation.title;
	}

	setTitle = (value: string) =>
	{
		if ( this.parent )
		{
			this.parent.userData.annotation.title = value;

			if (Scenario.currentStep()?.id)
			{
				this.modifiedTitle = TextVariables.textToData(value, {
					partName: this.parent.name,
					stepId: Scenario.currentStep()?.id,
					sequenceId: Scenario.currentSequence()?.id,
					metaData: this.parent.userData?.metaData
				});
			}
			else
			{
				this.modifiedTitle = "";
			}
			this.updateCanvas();
		}
	}

	getScale = () =>
	{
		return this.parent?.userData.annotation.scale;
	}

	setScale = (value: number) =>
	{
		this.spriteScale.set(value * this.widthScale, value * this.heightScale, 1);
		if ( this.parent)
		{
			this.parent.userData.annotation.scale = value;
		}
	}

	setAutoWidth = (value: boolean) =>
	{
		if (this.parent)
		{
			this.parent.userData.annotation.autoWidth = value;
			this.updateCanvas();
		}
	}

	getAutoWidth = () =>
	{
		return this.parent?.userData.annotation.autoWidth;
	}

	getWidth = () =>
	{
		return this.parent?.userData.annotation.width;
	}

	setWidth = (value: number) =>
	{
		if(this.parent)
		{
			this.parent.userData.annotation.width = value;
			this.updateCanvas();
		}
	}

	getDescription = () =>
	{
		return this.parent?.userData.annotation.description;
	}

	setDescription = (value: string) =>
	{
		if(this.parent)
		{
			this.parent.userData.annotation.description = value;
			if (Scenario.currentStep()?.id)
			{
				this.modifiedDescription = TextVariables.textToData(value, {
					partName: this.parent.name,
					stepId: Scenario.currentStep()?.id,
					sequenceId: Scenario.currentSequence()?.id,
					metaData: this.parent.userData?.metaData
				});
			}
			else
			{
				this.modifiedDescription = "";
			}
			this.updateCanvas();
		}
	}

	getTextColor = () =>
	{
		return this.parent?.userData.annotation.textColor;
	}

	setTextColor = (value: IColor) =>
	{
		if ( this.parent )
		{
			this.parent.userData.annotation.textColor.r = value.r;
			this.parent.userData.annotation.textColor.g = value.g;
			this.parent.userData.annotation.textColor.b = value.b;
			this.updateCanvas();
		}
	}

	getBackgroundColor = () =>
	{
		return this.parent?.userData.annotation.backgroundColor;
	}

	setBackgroundColor = (value: IColor) =>
	{
		if (this.parent)
		{
			this.parent.userData.annotation.backgroundColor.r = value.r;
			this.parent.userData.annotation.backgroundColor.g = value.g;
			this.parent.userData.annotation.backgroundColor.b = value.b;
			this.parent.userData.annotation.backgroundColor.a = value.a;
			this.updateCanvas();
		}
	}

	getLineColor = () =>
	{
		return this.parent?.userData.annotation.lineColor;
	}


	setLineColor = (value: IColor) =>
	{
		(this.calloutLine.material as THREE.MeshBasicMaterial).color.setRGB(value.r / 255, value.g / 255, value.b / 255, THREE.SRGBColorSpace);
		(this.calloutLine.material as THREE.MeshBasicMaterial).opacity = value.a! / 100;
	
		if (this.parent)
		{
			this.parent.userData.annotation.lineColor.r = value.r;
			this.parent.userData.annotation.lineColor.g = value.g;
			this.parent.userData.annotation.lineColor.b = value.b;
			this.parent.userData.annotation.lineColor.a = value.a;
			this.updateCanvas();
		}
	}

	getTitleColor = () =>
	{
		return this.parent?.userData.annotation.titleColor;
	}

	setTitleColor = (value: IColor) =>
	{
		if (this.parent)
		{
			this.parent.userData.annotation.titleColor.r = value.r;
			this.parent.userData.annotation.titleColor.g = value.g;
			this.parent.userData.annotation.titleColor.b = value.b;
			this.updateCanvas();
		}
	}

	getTitleBackground = () =>
	{
		return this.parent?.userData.annotation.titleBackground;
	}

	setTitleBackground = (value: IColor) =>
	{
		if ( this.parent)
		{
			const titleBackground = this.parent.userData.annotation.titleBackground;
			titleBackground.r = value.r;
			titleBackground.g = value.g;
			titleBackground.b = value.b;
			titleBackground.a = value.a;
			this.updateCanvas();
		}
	}

	getOffset = () =>
	{
		return this.parent?.userData.annotation.offset;
	}

	setOffset = (value: IVector3) =>
	{
		if (this.parent)
		{
			this.parent.userData.annotation.offset = { x: value.x, y: value.y, z: value.z };
		}
	}

	getOriginOffset = () =>
	{
		return this.parent?.userData.annotation.originOffset;
	}

	setOriginOffset = (value: IVector3) =>
	{
		if (this.parent)
		{
			this.parent.userData.annotation.originOffset = { x: value.x, y: value.y, z: value.z };
		}
	}

	setTitleTextAlign = (value: TextAlign) =>
	{
		if(this.parent)
		{
			this.parent.userData.annotation.titleTextAlign = value;
			this.updateCanvas();
		}
	}

	getTitleTextAlign = () =>
	{
		return this.parent?.userData.annotation.titleTextAlign;
	}

	setDescriptionTextAlign = (value: TextAlign) =>
	{
		if(this.parent)
		{
			this.parent.userData.annotation.descriptionTextAlign = value;
			this.updateCanvas();
		}
	}

	getDescriptionTextAlign = () =>
	{
		return this.parent?.userData.annotation.descriptionTextAlign;
	}

	setDescriptionEnabled = (value: boolean) =>
	{
		if(this.parent)
		{
			this.parent.userData.annotation.descriptionEnabled = value;
			this.updateCanvas();
		}
	}

	getDescriptionEnabled = () =>
	{
		return this.parent?.userData.annotation.descriptionEnabled;
	}

	setStrokeEnabled = (value: boolean) =>
	{
		this.strokeMaterial.visible = value;

		if(this.parent)
		{
			this.parent.userData.annotation.strokeEnabled = value;
			this.updateCanvas();
		}
	}

	getStrokeEnabled = () =>
	{
		return this.parent?.userData.annotation.strokeEnabled;
	}

	setStrokeColor = (value: IColor) =>
	{
		this.strokeMaterial.color.setRGB(value.r / 255, value.g / 255, value.b / 255, THREE.SRGBColorSpace);
		this.strokeMaterial.opacity = value.a! / 100;
		
		if(this.parent)
		{
			this.parent.userData.annotation.strokeColor.r = value.r;
			this.parent.userData.annotation.strokeColor.g = value.g;
			this.parent.userData.annotation.strokeColor.b = value.b;
			this.parent.userData.annotation.strokeColor.a = value.a;
		}
	}

	getStrokeColor = () =>
	{
		return this.parent?.userData.annotation.strokeColor;
	}

	copy = (source: this) =>
	{
		THREE.Object3D.prototype.copy.call( this, source, false );
		if ( source.center !== undefined ) this.center.copy( source.center );

		this.spriteScale.copy(source.spriteScale);
		if ( source.parent)
		{
			const annotation = source.parent.userData.annotation;
			this.setLineColor(annotation.lineColor);
			this.setStrokeColor(annotation.strokeColor);
			this.setStrokeEnabled(annotation.strokeEnabled);
		}
		return this;
	}

	updateMatrixWorld = (force: boolean) =>
	{
		if (this.matrixAutoUpdate) this.updateMatrix();

		if (this.matrixWorldNeedsUpdate || force)
		{
			if (this.parent === null)
			{
				this.matrixWorld.copy(this.matrix);

			}
			else
			{
				this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
			}

			this.matrixWorldNeedsUpdate = false;

			force = true;

		}

		if ( this.parent )
		{

			const originWorldPosition = new THREE.Vector3(this.parent.userData.annotation.originOffset.x, this.parent.userData.annotation.originOffset.y, this.parent.userData.annotation.originOffset.z);
			this.parent.localToWorld(originWorldPosition);

			const calloutWorldPosition = new THREE.Vector3(this.parent.userData.annotation.offset.x, this.parent.userData.annotation.offset.y, this.parent.userData.annotation.offset.z).add(originWorldPosition);
			this.matrixWorld.compose(calloutWorldPosition, identityQuat, this.spriteScale);

			const camera = Engine.getInstance().getRenderCamera();
			if (!camera) return;

			cameraUp.set(0, 1, 0).applyQuaternion(camera.quaternion);
			cameraRight.set(1, 0, 0).applyQuaternion(camera.quaternion);

			let calloutHalfWidth = this.spriteScale.x / 2;
			let calloutHalfHeight = this.spriteScale.y / 2;

			if(this.parent.userData.annotation.strokeEnabled)
			{
				const halfStrokeWidth = strokeWidth / 2 * this.parent.userData.annotation.scale;
				const strokePlanePositions = [
					calloutWorldPosition.clone().add(cameraRight.clone().multiplyScalar(calloutHalfWidth + halfStrokeWidth)),
					calloutWorldPosition.clone().sub(cameraRight.clone().multiplyScalar(calloutHalfWidth + halfStrokeWidth)),
					calloutWorldPosition.clone().add(cameraUp.clone().multiplyScalar(calloutHalfHeight + halfStrokeWidth)),
					calloutWorldPosition.clone().sub(cameraUp.clone().multiplyScalar(calloutHalfHeight + halfStrokeWidth))
				];

				const strokePlaneScales = [
					new THREE.Vector3(strokeWidth * this.parent.userData.annotation.scale, this.spriteScale.y + (strokeWidth * 2 * this.parent.userData.annotation.scale), 1),
					new THREE.Vector3(strokeWidth * this.parent.userData.annotation.scale, this.spriteScale.y + (strokeWidth * 2 * this.parent.userData.annotation.scale), 1),
					new THREE.Vector3(this.spriteScale.x, strokeWidth * this.parent.userData.annotation.scale, 1),
					new THREE.Vector3(this.spriteScale.x, strokeWidth * this.parent.userData.annotation.scale, 1)
				];

				for(let i = 0; i < 4; i++)
				{
					this.strokePlanes[i].matrixWorld.compose(strokePlanePositions[i], camera.quaternion, strokePlaneScales[i]);
				}
			}


			this.calloutLine.visible = originWorldPosition.distanceTo(calloutWorldPosition) > 0.001;
			if (!this.calloutLine.visible) return;

			if(this.parent.userData.annotation.strokeEnabled)
			{
				calloutHalfWidth += strokeWidth * this.parent.userData.annotation.scale;
				calloutHalfHeight += strokeWidth * this.parent.userData.annotation.scale;
			}
			//find the world space coordinates of the callout corners, project them to NDC space and construct a 2d rectangle
			const calloutTopLeft = calloutWorldPosition.clone();
			calloutTopLeft.sub(cameraRight.clone().multiplyScalar(calloutHalfWidth));
			calloutTopLeft.add(cameraUp.clone().multiplyScalar(calloutHalfHeight));

			const calloutBottomRight = calloutWorldPosition.clone();
			calloutBottomRight.add(cameraRight.clone().multiplyScalar(calloutHalfWidth));
			calloutBottomRight.sub(cameraUp.clone().multiplyScalar(calloutHalfHeight));

			const calloutTopLeft2d = calloutTopLeft.clone().project(camera);
			const calloutBottomRight2d = calloutBottomRight.clone().project(camera);
			const calloutRect2d = { top: calloutTopLeft2d.y, bottom: calloutBottomRight2d.y, left: calloutTopLeft2d.x, right: calloutBottomRight2d.x };

			const originWorldPosition2d = originWorldPosition.clone().project(camera);
			const originWorldPosition2dVector2 = new THREE.Vector2(originWorldPosition2d.x, originWorldPosition2d.y);

			//if the line's origin is within the callout rectangle, don't draw it at all, as it would be fully occluded anyway
			if(MathUtils.isPointWithinRect(originWorldPosition2dVector2, calloutRect2d))
			{
				this.calloutLine.visible = false;
				return;
			}

			const calloutWorldPosition2d = calloutWorldPosition.clone();
			calloutWorldPosition2d.project(camera);

			let lineEnd;
			const intersection = MathUtils.getLineRectengaleIntersection(calloutRect2d, originWorldPosition2d, calloutWorldPosition2d);
			if (intersection)
			{
				const intersection2d = new THREE.Vector3(intersection.x, intersection.y, calloutWorldPosition2d.z);

				if(originWorldPosition2d.z > 1.0)
				{
				//at certain angles, projecting the origin to NDC space will place the point in the flipped direction along the same expected line
				//in which case we move the intersection point to the opposite side of the callout, relative to the callout's center
					const towardsCallout = new THREE.Vector3().subVectors(calloutWorldPosition2d, intersection2d);
					intersection2d.x = calloutWorldPosition2d.x + towardsCallout.x;
					intersection2d.y = calloutWorldPosition2d.y + towardsCallout.y;
				}
				const intersectionWorldPosition = intersection2d.clone().unproject(camera);
				lineEnd = intersectionWorldPosition;
			}
			else
			{
			//callout is outside of camera's view. just draw a line directly towards its center
				lineEnd = calloutWorldPosition;
			}

			const lineStart = originWorldPosition;

			const lineLength = lineStart.distanceTo(lineEnd);
			const lineDirection = new THREE.Vector3().subVectors(lineEnd, lineStart).normalize();

			//position the line halfway between start & end points
			const lineWorldPosition = lineStart.clone().add(lineDirection.multiplyScalar(lineLength / 2));

			//scale according to distance between point
			const lineWorldScale = new THREE.Vector3(lineWidthConstant * this.parent.userData.annotation.scale, lineLength, lineWidthConstant * this.parent.userData.annotation.scale);

			//rotate line so its up direction points towards the callout
			const lineWorldRotation = new THREE.Quaternion().setFromRotationMatrix(tempMat.lookAt(lineWorldPosition, lineEnd, this.calloutLine.up));
			lineWorldRotation.multiply(lookAtRotationCorrection);

			this.calloutLine.matrixWorld.compose(lineWorldPosition, lineWorldRotation, lineWorldScale);
		}
	}
}


export default Callout;
